/**
 * City-distance daily scoring. Distance is the underlying signal, score
 * is the displayed surface — leaderboards, end-screen totals, share
 * grids, and the daily stats page all key off score.
 *
 * Why a score on top of raw km:
 *   - Lets us cap a "perfect" zone (8 mi radius) so close-but-not-exact
 *     guesses don't get punished by sub-km hairsplitting
 *   - Keeps the visible number bounded at 100 — easier to read and rank
 *     than five-digit km totals
 *   - Score per round is integer 0–100, so /500 over five rounds reads
 *     as a clean test grade
 *
 * Curve: polynomial blend `t³ × (0.6 + 0.4 t²)`, where
 *
 *   t = (MAX - km) / (MAX - PERFECT)        // 1 at perfect edge, 0 at antipode
 *   score = 99 * t³ * (0.6 + 0.4 * t²)
 *
 * Equivalent to `0.6 t³ + 0.4 t⁵`. Linear scoring was "too generous"
 * (200 km miss → 98); pure t³ still left 100 km at 98; pure t⁴ matches
 * MapTap's curve, which we explicitly avoid copying. The 60/40 cubic-
 * quintic blend hits 100 km → 97 with a polynomial that's distinct
 * from any single integer power.
 *
 * Basic arithmetic only — keeps the SQL mirror portable across SQLite
 * (test) and MySQL (prod) without depending on POW/EXP/SQRT.
 *
 * Tuning knobs:
 *   PERFECT_RADIUS_KM — anything inside this zone scores 100.
 *   MAX_DISTANCE_KM — the antipode (half-circumference of a 6371-km
 *     sphere). Score-0 anchor.
 *   Blend weights (0.6, 0.4) — hard-coded; rebalance if mid-range
 *     needs softening or harshening.
 */

const EARTH_RADIUS_KM = 6371;
export const PERFECT_RADIUS_KM = 8 * 1.609344; // 8 miles in km, exact
export const MAX_DISTANCE_KM = Math.PI * EARTH_RADIUS_KM;

/**
 * Compute the integer score (0–100) for a single city-distance round.
 * Cubic-quintic blend, clamped at both ends. Distances outside
 * [0, MAX_DISTANCE_KM] clamp to the nearest endpoint — defensive, since
 * haversine should never exceed the half-circumference but a numerical
 * edge could slip past.
 */
export function cityDailyScore(km: number): number {
  if (!Number.isFinite(km) || km <= PERFECT_RADIUS_KM) return 100;
  if (km >= MAX_DISTANCE_KM) return 0;
  const t = (MAX_DISTANCE_KM - km) / (MAX_DISTANCE_KM - PERFECT_RADIUS_KM);
  const tt = t * t;
  const ttt = tt * t;
  return Math.max(0, Math.min(99, Math.round(99 * ttt * (0.6 + 0.4 * tt))));
}

/**
 * Render a daily run's total score as a 0..100 average per round, fixed
 * to 2 decimals. The displayed surface for every daily readout — share
 * grid, leaderboard, end screen, stats card, public profile — so a
 * player sees the same number shape regardless of which page surfaces
 * it.
 *
 * Storage stays integer per-round (cityDailyScore returns 0..100 ints,
 * the server stores their km originals); this function just averages
 * the round count out at display time.
 *
 * Returns "—" for missing data (null/undefined total or zero rounds)
 * so callers can drop the helper into JSX/Svelte expressions without a
 * separate nullish-coalesce. Always 2 decimal places — even on whole
 * numbers ("100.00") — so column alignment in tables stays clean.
 */
export function formatDailyAverageScore(
  totalScore: number | null | undefined,
  rounds: number,
): string {
  if (totalScore === null || totalScore === undefined || rounds <= 0) return '—';
  return (totalScore / rounds).toFixed(2);
}
